import { green } from '@mui/material/colors';
import type { PaletteMode, PaletteOptions } from '@mui/material';
import type { ThemeOptions } from '@mui/material/styles';

/**
 * Dispatch theme source. `getThemeOptions(mode)` is the single entry point —
 * see `ThemeModeProvider`, the only owner of the mode value. Standard MUI
 * palette-mode theming handles most colors; the `SemanticTokens` below hold
 * the few the app reads directly from the palette for meaning.
 */

/**
 * Semantic tokens the app reads directly from the palette for meaning (as
 * opposed to standard MUI palette-mode theming). Currently just the map-pin
 * cluster badge, which is drawn on the always-light basemap.
 */
type SemanticTokens = {
  /** Map pin cluster badge fill (drawn on the always-light basemap). */
  mapPin: {
    cluster: string;
  };
};

/**
 * Map semantic tokens. The Mapbox basemap — and everything Dispatch draws on
 * it — is intentionally left LIGHT in both palette modes (product decision: the
 * map is excluded from dark theming), so these values are mode-independent.
 * There is deliberately no separate dark variant; a single constant makes that
 * intent explicit rather than implying a divergence that does not exist.
 */
const MAP_SEMANTIC: SemanticTokens = {
  mapPin: {
    cluster: '#1876d1',
  },
};

/** App bar / nav surface color — kept populated in BOTH modes because the
 * module augmentation makes `dark` required and DarkBox / AuthRedirect /
 * MuiAppBar read `palette.dark.main`. */
const APP_BAR_DARK = '#202632';

function getPalette(mode: PaletteMode): PaletteOptions {
  const semantic = MAP_SEMANTIC;

  if (mode === 'dark') {
    return {
      mode: 'dark',
      dark: {
        main: APP_BAR_DARK,
      },
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
      semantic,
    };
  }

  return {
    mode: 'light',
    dark: {
      main: APP_BAR_DARK,
    },
    semantic,
  };
}

/**
 * Builds the MUI theme options for the requested palette mode. Pure in `mode`
 * — the only source of light/dark divergence in the whole app.
 *
 * @param mode - `'light'` or `'dark'`
 * @returns MUI ThemeOptions for that mode
 */
export function getThemeOptions(mode: PaletteMode): ThemeOptions {
  return {
    palette: getPalette(mode),
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.dark.main,
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          // `label.Mui-focused` is routed to text.secondary so it stays the
          // original muted #00000099 in light and stays legible in dark.
          root: ({ theme }) => ({
            '& label.Mui-focused': {
              color: theme.palette.text.secondary,
            },
            '& .MuiInput-underline:after': {
              borderBottomColor: '#303F9F',
            },
          }),
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: green[500],
            '&.Mui-checked': {
              color: green[500],
            },
          },
        },
      },
    },
    typography: {
      fontFamily: ['"PT Sans"', 'sans-serif'].join(','),
    },
  };
}

export type { Theme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    dark: Palette['primary'];
    semantic: SemanticTokens;
  }
  interface PaletteOptions {
    dark: PaletteOptions['primary'];
    semantic: SemanticTokens;
  }
}
